import { __extends } from "tslib";
import { getShadowElement, emitEvent } from '@tyler-components-web/core';
import { getActiveElement } from 'shadow-utils';
import { BaseAdapter } from '../core';
import { TIME_PICKER_CONSTANTS } from './time-picker-constants';
import { TimeInputMask } from '../core/mask/time-input-mask';
import { TEXT_FIELD_CONSTANTS } from '../text-field';
import { ICON_BUTTON_CONSTANTS } from '../icon-button';
import { ICON_CONSTANTS } from '../icon';
import { ListDropdown } from '../list-dropdown';
var TimePickerAdapter = /** @class */ (function (_super) {
    __extends(TimePickerAdapter, _super);
    function TimePickerAdapter(component) {
        return _super.call(this, component) || this;
    }
    TimePickerAdapter.prototype.initialize = function () {
        this._inputElement = this._component.querySelector(TIME_PICKER_CONSTANTS.selectors.INPUT);
    };
    TimePickerAdapter.prototype.initializeMask = function (options) {
        if (this._inputMask) {
            this._inputMask.destroy();
        }
        this._inputMask = new TimeInputMask(this._inputElement, options);
    };
    TimePickerAdapter.prototype.destroy = function () {
        this._targetElement = undefined;
        this._toggleElement = undefined;
        this._inputElement = undefined;
    };
    TimePickerAdapter.prototype.destroyMask = function () {
        if (this._inputMask) {
            this._inputMask.destroy();
            this._inputMask = undefined;
        }
    };
    TimePickerAdapter.prototype.initializeAccessibility = function (identifier) {
        this._inputElement.setAttribute('autocomplete', 'off');
        this._inputElement.setAttribute('autocorrect', 'off');
        this._inputElement.setAttribute('autocapitalize', 'off');
        this._inputElement.setAttribute('spellcheck', 'false');
        this._inputElement.setAttribute('role', 'combobox');
        this._inputElement.setAttribute('aria-live', 'assertive');
        this._inputElement.setAttribute('aria-atomic', 'true');
        this._inputElement.setAttribute('aria-haspopup', 'true');
        this._inputElement.setAttribute('aria-expanded', 'false');
    };
    TimePickerAdapter.prototype.addInputListener = function (type, listener) {
        this._inputElement.addEventListener(type, listener);
    };
    TimePickerAdapter.prototype.removeInputListener = function (type, listener) {
        if (this._inputElement) {
            this._inputElement.removeEventListener(type, listener);
        }
    };
    TimePickerAdapter.prototype.addToggleListener = function (type, listener) {
        if (this._toggleElement) {
            this._toggleElement.addEventListener(type, listener);
        }
    };
    TimePickerAdapter.prototype.removeToggleListener = function (type, listener) {
        if (this._toggleElement) {
            this._toggleElement.removeEventListener(type, listener);
        }
    };
    TimePickerAdapter.prototype.hasInputElement = function () {
        return !!this._inputElement;
    };
    TimePickerAdapter.prototype.tryCreateToggle = function () {
        var tcwTextField = this._component.querySelector(TEXT_FIELD_CONSTANTS.elementName);
        var toggleElement = this._component.querySelector(TIME_PICKER_CONSTANTS.selectors.TOGGLE);
        if (tcwTextField) {
            var existingIconButton = tcwTextField.querySelector(ICON_BUTTON_CONSTANTS.elementName + "[slot=trailing]");
            if (existingIconButton || toggleElement) {
                this._toggleElement = (existingIconButton || toggleElement);
                return;
            }
            var iconButtonElement = document.createElement(ICON_BUTTON_CONSTANTS.elementName);
            iconButtonElement.slot = 'trailing';
            iconButtonElement.dense = true;
            iconButtonElement.densityLevel = 3;
            iconButtonElement.style.marginRight = '4px'; // Override default trailing slot margin in text-field
            var buttonElement = document.createElement('button');
            buttonElement.type = 'button';
            buttonElement.tabIndex = -1;
            buttonElement.setAttribute('aria-label', 'Toggle time dropdown');
            var iconElement = document.createElement(ICON_CONSTANTS.elementName);
            iconElement.name = 'clock_outline';
            buttonElement.appendChild(iconElement);
            iconButtonElement.appendChild(buttonElement);
            tcwTextField.appendChild(iconButtonElement);
            this._toggleElement = iconButtonElement;
        }
        else if (toggleElement) {
            this._toggleElement = toggleElement;
        }
    };
    TimePickerAdapter.prototype.tryFocusInput = function () {
        this._inputElement.select();
    };
    TimePickerAdapter.prototype.tryBlurInput = function () {
        this._inputElement.blur();
    };
    TimePickerAdapter.prototype.selectInputText = function () {
        this._inputElement.select();
    };
    TimePickerAdapter.prototype.isInputDisabled = function () {
        return this._inputElement.disabled;
    };
    TimePickerAdapter.prototype.isInputFocused = function () {
        return getActiveElement() === this._inputElement;
    };
    TimePickerAdapter.prototype.setInputValue = function (value, emitEvents) {
        if (this._inputElement.value === value) {
            return;
        }
        this._inputElement.value = value;
        if (this._inputMask) {
            this._inputMask.update();
        }
        if (emitEvents) {
            this._emitInputEvent('change');
            this._emitInputEvent('input');
        }
    };
    TimePickerAdapter.prototype.getInputValue = function () {
        return this._inputMask ? this._inputMask.maskedValue : this._inputElement.value;
    };
    TimePickerAdapter.prototype.setDisabled = function (isDisabled) {
        this._inputElement.disabled = isDisabled;
        this._inputElement.setAttribute('aria-disabled', isDisabled.toString());
        this.setToggleDisabled(isDisabled);
    };
    TimePickerAdapter.prototype.attachDropdown = function (config) {
        this._listDropdown = new ListDropdown(this._inputElement, config);
        this._listDropdown.open();
        this._inputElement.setAttribute('aria-controls', "list-dropdown-popup-" + config.id);
    };
    TimePickerAdapter.prototype.detachDropdown = function () {
        if (this._listDropdown) {
            this._listDropdown.close();
            this._listDropdown.destroy();
            this._listDropdown = undefined;
        }
        this._inputElement.removeAttribute('aria-controls');
    };
    TimePickerAdapter.prototype.propagateKey = function (key) {
        if (this._listDropdown) {
            this._listDropdown.handleKey(key);
        }
    };
    TimePickerAdapter.prototype.setActiveDescendant = function (id) {
        if (id) {
            this._inputElement.setAttribute('aria-activedescendant', id);
        }
        else {
            this._inputElement.removeAttribute('aria-activedescendant');
        }
    };
    TimePickerAdapter.prototype.getTargetElementWidth = function (selector) {
        var targetElement = this._getTargetElement(selector);
        return targetElement.getBoundingClientRect().width;
    };
    TimePickerAdapter.prototype._emitInputEvent = function (type) {
        this._inputElement.dispatchEvent(new Event(type));
    };
    TimePickerAdapter.prototype.emitInputEvent = function (type, data) {
        emitEvent(this._inputElement, type, data);
    };
    TimePickerAdapter.prototype.setInputReadonly = function (value) {
        this._inputElement.readOnly = value;
    };
    TimePickerAdapter.prototype.setToggleDisabled = function (value) {
        if (this._toggleElement) {
            this._toggleElement.setAttribute('aria-disabled', value.toString());
            if ('disabled' in this._toggleElement) {
                this._toggleElement.disabled = value;
            }
            else {
                var button = this._toggleElement.querySelector('button');
                if (button) {
                    button.disabled = value;
                }
            }
        }
    };
    TimePickerAdapter.prototype._getTargetElement = function (selector) {
        if (this._targetElement) {
            return this._targetElement;
        }
        this._targetElement = selector ? this._component.querySelector(selector) || this._getDefaultTargetElement() : this._getDefaultTargetElement();
        return this._targetElement;
    };
    TimePickerAdapter.prototype._getDefaultTargetElement = function () {
        // This component is often used with the text-field, if so, let's target our popup around one if its internal elements for proper alignnment
        var textField = this._component.querySelector(TEXT_FIELD_CONSTANTS.elementName);
        if (textField && textField.shadowRoot) {
            var textFieldRoot = getShadowElement(textField, TEXT_FIELD_CONSTANTS.selectors.ROOT);
            if (textFieldRoot) {
                return textFieldRoot;
            }
        }
        return this._component; // Otherwise we just use the time-picker host as the target
    };
    return TimePickerAdapter;
}(BaseAdapter));
export { TimePickerAdapter };
